/**
 * The letter's data model.
 *
 * A separate model from the site builder's blocks, and that is the point rather
 * than duplication. `@wa/widgets` renders for a browser: flexbox, CSS variables,
 * classes, media queries. An email is rendered by Outlook's Word engine and by
 * Gmail's HTML stripper, neither of which has any of those. So the block set
 * here is small, every property maps to something that survives a mail client,
 * and there is exactly one nesting level (columns hold leaves, and nothing holds
 * columns) because nested tables are where email layouts break.
 *
 * React-free on purpose: the renderer, the plain-text fallback and their tests
 * all import this file, and none of them should need a DOM.
 */

/** Blocks that can sit at the top level of a letter. */
export type BlockType =
  | "heading"
  | "text"
  | "button"
  | "image"
  | "divider"
  | "spacer"
  | "columns"
  | "quote"
  | "list"
  | "social"
  | "signature"
  | "footer"
  | "html";

/** Blocks allowed inside a column. `columns` is absent deliberately - see the
 *  file comment. `footer` too: a letter has one, and it belongs at the bottom. */
export const COLUMN_CHILD_TYPES = [
  "heading", "text", "button", "image", "divider", "spacer", "quote", "list", "html",
] as const;

export type ColumnChildType = (typeof COLUMN_CHILD_TYPES)[number];

export interface Block {
  id: string;
  type: BlockType;
  props: Record<string, unknown>;
}

export interface ColumnSlot {
  id: string;
  children: Block[];
}

export type Align = "left" | "center" | "right";

/** Letter-wide settings. Everything a single block cannot decide on its own. */
export interface PageStyle {
  /** Behind the card. Most clients show it as the letter's margin. */
  backgroundColor: string;
  /** The card itself. */
  cardColor: string;
  /** Card width in px. 600 is the number every mail client agrees on; above
   *  ~640 the letter starts scrolling sideways on a phone. */
  contentWidth: number;
  /** Only web-safe families reach Outlook. The picker offers a short list. */
  fontFamily: string;
  fontSize: number;
  textColor: string;
  linkColor: string;
  /** Card corner radius. Rounded corners need the radius on a `<td>`, not on a
   *  wrapping `<div>` - Gmail on Android drops it from the div. */
  radius: number;
  paddingX: number;
  paddingY: number;
}

export interface EmailDesign {
  /** Bumped only when an old design can no longer be read as-is. */
  version: 1;
  page: PageStyle;
  blocks: Block[];
}

export const DESIGN_VERSION = 1 as const;

/** Web-safe stacks. Anything else silently falls back in Outlook, which means
 *  the letter an operator approves is not the letter that arrives. */
export const FONT_STACKS: { id: string; label: string; stack: string }[] = [
  { id: "system", label: "System", stack: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif" },
  { id: "arial", label: "Arial", stack: "Arial,Helvetica,sans-serif" },
  { id: "georgia", label: "Georgia", stack: "Georgia,'Times New Roman',serif" },
  { id: "times", label: "Times", stack: "'Times New Roman',Times,serif" },
  { id: "verdana", label: "Verdana", stack: "Verdana,Geneva,sans-serif" },
  { id: "trebuchet", label: "Trebuchet", stack: "'Trebuchet MS',Tahoma,sans-serif" },
  { id: "courier", label: "Courier", stack: "'Courier New',Courier,monospace" },
];

export function fontStack(id: string): string {
  return FONT_STACKS.find((f) => f.id === id)?.stack ?? FONT_STACKS[0]!.stack;
}

export const DEFAULT_PAGE: PageStyle = {
  backgroundColor: "#f4f5f7",
  cardColor: "#ffffff",
  contentWidth: 600,
  fontFamily: "system",
  fontSize: 16,
  textColor: "#1f2933",
  linkColor: "#2563eb",
  radius: 12,
  paddingX: 32,
  paddingY: 32,
};

/**
 * Every property a block understands, with a value.
 *
 * Complete on purpose: the inspector reads these to know what to draw, and the
 * renderer never has to guess what an absent key meant. A block added to a
 * letter last month must keep rendering the same way when a new property lands,
 * which is why the renderer falls back to these rather than to `""`.
 */
export const DEFAULT_BLOCK_PROPS: Record<BlockType, Record<string, unknown>> = {
  heading: {
    text: "Heading",
    level: "h2" as "h1" | "h2" | "h3",
    align: "left" as Align,
    color: "",
    fontSize: 0,
    paddingTop: 8,
    paddingBottom: 8,
  },
  text: {
    // Rich text from the shared editor. Stored as HTML because that is what
    // both the editor and the letter speak.
    html: "<p>Write the letter here.</p>",
    align: "left" as Align,
    color: "",
    fontSize: 0,
    lineHeight: 1.6,
    paddingTop: 8,
    paddingBottom: 8,
  },
  button: {
    text: "See the findings",
    url: "https://husly.app",
    align: "left" as Align,
    backgroundColor: "#111827",
    textColor: "#ffffff",
    radius: 6,
    paddingTop: 12,
    paddingBottom: 12,
    // Not a Tailwind-style scale: these are the button's own inner paddings,
    // and a mail client honours them only as inline padding on the anchor.
    innerX: 24,
    innerY: 12,
    fullWidth: false,
  },
  image: {
    src: "",
    alt: "",
    href: "",
    align: "center" as Align,
    /** Percentage of the card width. Fixed px would overflow a phone. */
    width: 100,
    radius: 0,
    paddingTop: 8,
    paddingBottom: 8,
  },
  divider: {
    color: "#e4e7ec",
    thickness: 1,
    paddingTop: 16,
    paddingBottom: 16,
  },
  spacer: {
    height: 24,
  },
  columns: {
    count: 2,
    gap: 24,
    paddingTop: 8,
    paddingBottom: 8,
    cols: [
      { id: "col-1", children: [] as Block[] },
      { id: "col-2", children: [] as Block[] },
    ] as ColumnSlot[],
  },
  quote: {
    text: "Their site runs a core three major versions behind.",
    author: "",
    borderColor: "#2563eb",
    backgroundColor: "#f8fafc",
    color: "#334155",
    paddingTop: 8,
    paddingBottom: 8,
  },
  list: {
    // One item per line. A textarea beats a repeating field row here: the
    // operator is pasting scanner findings, not filling a form.
    items: "Core three versions behind\nNine plugins out of date\nNo TLS on the contact form",
    marker: "•",
    color: "",
    align: "left" as Align,
    paddingTop: 8,
    paddingBottom: 8,
  },
  social: {
    links: [
      { id: "linkedin", label: "LinkedIn", url: "" },
      { id: "facebook", label: "Facebook", url: "" },
    ],
    align: "center" as Align,
    color: "#64748b",
    paddingTop: 8,
    paddingBottom: 8,
  },
  signature: {
    name: "",
    title: "",
    email: "",
    phone: "",
    avatar: "",
    align: "left" as Align,
    paddingTop: 16,
    paddingBottom: 8,
  },
  footer: {
    // The unsubscribe placeholder is not decoration. A commercial message has
    // to carry a free and easy way out, and this block is the one that puts it
    // there without the operator remembering the exact token.
    text: "You are getting this because your company is in the Estonian business register.",
    unsubscribeText: "I would rather not hear from you again",
    address: "",
    color: "#98a2b3",
    fontSize: 12,
    align: "center" as Align,
    paddingTop: 24,
    paddingBottom: 8,
  },
  html: {
    html: "<p>Raw HTML. Only what a mail client understands: tables, inline styles.</p>",
    paddingTop: 8,
    paddingBottom: 8,
  },
};

/** Ids are only unique within one design, so a counter would collide the
 *  moment two designs are merged. Short random suffix, no dependency. */
export function newBlockId(): string {
  return `b${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Give every column in a props bag a fresh id, in place.
 *
 * Columns are addressed by id when something is dropped into one, so two
 * `columns` blocks holding a column called `col-1` is not a cosmetic clash - a
 * block dropped into the first block's left column lands in BOTH. The defaults
 * above have to name their columns something, so every path that produces a
 * block renames them: creating one, copying one, and reading a design back.
 */
function freshenColumnIds(props: Record<string, unknown>): void {
  const cols = props.cols;
  if (!Array.isArray(cols)) return;
  props.cols = (cols as ColumnSlot[]).map((c) => ({
    id: newBlockId(),
    children: (c.children ?? []).map(cloneBlock),
  }));
}

/** A block with its defaults filled in. Deep-copied, otherwise two blocks of
 *  the same type would share one `cols` array and editing one would edit both. */
export function makeBlock(type: BlockType): Block {
  const props = structuredClone(DEFAULT_BLOCK_PROPS[type]);
  freshenColumnIds(props);
  return { id: newBlockId(), type, props };
}

/** A copy under fresh ids, children included. Used by duplicate and by applying
 *  a template - a design that keeps the source's ids would have two blocks
 *  answering to the same selection. */
export function cloneBlock(block: Block): Block {
  const props = structuredClone(block.props);
  freshenColumnIds(props);
  return { id: newBlockId(), type: block.type, props };
}

export function emptyDesign(): EmailDesign {
  return { version: DESIGN_VERSION, page: { ...DEFAULT_PAGE }, blocks: [] };
}

/**
 * Read a stored design back, filling in anything a older version did not have.
 *
 * Returns null for anything that is not a design, which is the honest answer
 * when the column holds hand-written JSON or a shape from a future version -
 * the editor then offers to start a new one rather than rendering a wreck.
 */
export function parseDesign(raw: unknown): EmailDesign | null {
  if (!raw || typeof raw !== "object") return null;
  const d = raw as Partial<EmailDesign>;
  if (!Array.isArray(d.blocks)) return null;

  // Ids are kept as stored wherever they are unique. Handing back different
  // ids than were saved would make the campaign editor believe the letter
  // changed the moment it was opened, and offer to save an edit nobody made.
  const seen = new Set<string>();
  const keepOrFresh = (id: unknown): string => {
    const s = typeof id === "string" && id ? id : newBlockId();
    const out = seen.has(s) ? newBlockId() : s;
    seen.add(out);
    return out;
  };

  function readBlock(b: Block): Block {
    const props: Record<string, unknown> = { ...DEFAULT_BLOCK_PROPS[b.type], ...(b.props ?? {}) };
    if (Array.isArray(props.cols)) {
      props.cols = (props.cols as ColumnSlot[]).map((c) => ({
        id: keepOrFresh(c?.id),
        children: (c?.children ?? []).filter(isBlockish).map(readBlock),
      }));
    }
    return { id: keepOrFresh(b.id), type: b.type, props };
  }

  return {
    version: DESIGN_VERSION,
    page: { ...DEFAULT_PAGE, ...(d.page ?? {}) },
    blocks: d.blocks.filter(isBlockish).map(readBlock),
  };
}

/** A stored entry that is at least shaped like a block of a type we know. An
 *  unknown type is dropped rather than rendered blank - a design from a newer
 *  editor should lose the block it cannot draw, not the whole letter. */
function isBlockish(b: unknown): b is Block {
  return (
    !!b &&
    typeof b === "object" &&
    typeof (b as Block).type === "string" &&
    (b as Block).type in DEFAULT_BLOCK_PROPS
  );
}

/** The props of a block, with any key the block did not store filled in from
 *  the defaults. Every renderer and inspector call goes through this. */
export function resolveProps(block: Block): Record<string, unknown> {
  return { ...DEFAULT_BLOCK_PROPS[block.type], ...block.props };
}
